import PrepaidCardApi from '@apis/prepaid/PrepaidCardApi';
import userPrepaidCardQueries from '@apis/queries/userPrepaidCardQueries';
import PrepaidContext from '@components/prepaid/PrepaidProvider/PrepaidContext';
import useCurrency from '@hooks/item/useCurrency';
import usePrepaidCardPayment from '@hooks/prepaidCard/usePrepaidCardPayment';
import useProjectId from '@hooks/useProjectId';
import { ErrorResponse } from '@models/error';
import { useMutation, useQuery } from '@tanstack/react-query';
import { prepaidCardCheckoutToGtmFormat, pushEcommerceEvent } from '@utils/gtm';
import { savePendingPurchase } from '@utils/pendingPurchaseUtils';
import { setIsPrepaidCardBalanceUpdated } from '@utils/prepaidCardUtils';
import { AxiosError } from 'axios';
import { useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

function usePrepaidCharge(id: string) {
    const { t } = useTranslation('prepaidCard');
    const { amount } = useContext(PrepaidContext);
    const projectId = useProjectId();
    const openPayment = usePrepaidCardPayment();
    const submitting = useRef(false);
    const currency = useCurrency();
    const { data: card } = useQuery(userPrepaidCardQueries.detail(id));

    const { mutate, isPending } = useMutation({
        mutationFn: () => PrepaidCardApi.chargePrepaidCard(id, {
            projectId,
            amount: amount * 10000,
            payMethodType: 'CREDIT_CARD',
        }),
        onSuccess(res) {
            setIsPrepaidCardBalanceUpdated(true);
            savePendingPurchase('PREPAID_CARD_CHARGE', {
                ...prepaidCardCheckoutToGtmFormat({
                    id,
                    name: card?.name || id,
                    amount: amount * 10000,
                    currency,
                    source: 'PREPAID_CARD_CHARGE',
                }),
                transaction_id: res.orderNo,
                purchase_type: 'PREPAID_CARD_CHARGE',
                payment_method: 'CREDIT_CARD',
            });
            openPayment(res, amount * 10000, `${t('charge.itemName')} ${(amount * 10000).toLocaleString()}${t('currencyUnit')}`);
        },
        onSettled() {
            submitting.current = false;
        },
        onError(e: AxiosError<ErrorResponse>) {
            toast.error(e.response?.data?.message || t('charge.error'));
        },
    });

    const handleCharge = () => {
        if (submitting.current) return;
        if (!Number.isSafeInteger(amount * 10000) || amount <= 0) {
            toast.error(t('charge.error'));
            return;
        }
        submitting.current = true;
        pushEcommerceEvent('add_payment_info', {
            ...prepaidCardCheckoutToGtmFormat({
                id,
                name: card?.name || id,
                amount: amount * 10000,
                currency,
                source: 'PREPAID_CARD_CHARGE',
            }),
            payment_type: 'CREDIT_CARD',
        });
        mutate();
    };

    return { handleCharge, isPending };
}

export default usePrepaidCharge;
